import { Landmark, Ruler, Sun, Calendar, Compass, Award, MapPin, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import Gallery from "@/components/Gallery";

// Asset URLs - Served locally to avoid CDN cross-origin issues
const ASSETS = {
  clockCloseup: "/images/reloj-ampliado.jpeg",
  clockContext: "/images/reloj-contexto.jpeg",
  clockSimulation: "/images/reloj-simulacion.bmp",
  clock3DModel: "/gear-3d-model.glb"
};

// Google Maps URL with direct coordinates - opens in new tab
const GOOGLE_MAPS_URL = "https://www.google.com/maps/place/41%C2%B038'16.0%22N+0%C2%B053'53.9%22W/@41.637778,-0.898302,17z";

const galleryImages = [
  {
    src: ASSETS.clockCloseup,
    alt: "Reloj Solar - Vista Detallada",
    description: "Detalle del diseño del reloj solar inspirado en el logotipo de Rotary"
  },
  {
    src: ASSETS.clockContext,
    alt: "Reloj Solar - Ubicación",
    description: "El reloj solar en su ubicación junto a la Cámara de Comercio de Zaragoza"
  },
  {
    src: ASSETS.clockSimulation,
    alt: "Simulación del Reloj Solar",
    description: "Diseño y simulación del proyecto del reloj solar"
  }
];

const specifications = [
  {
    icon: Ruler,
    label: "Altura",
    value: "2.5 metros",
    description: "Estructura imponente de acero inoxidable"
  },
  {
    icon: Sun,
    label: "Funcionalidad",
    value: "Horas Solares",
    description: "Marca la hora solar real mediante la sombra del gnomon"
  },
  {
    icon: Calendar,
    label: "Marcadores",
    value: "Solsticios y Equinoccios",
    description: "Indica los cambios de estación a lo largo del año"
  },
  {
    icon: Compass,
    label: "Orientación",
    value: "Norte Astronómico",
    description: "Alineado precisamente con el norte geográfico"
  }
];

const Monumento = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="monumento-page">
      {/* Header */}
      <header className="bg-rotary-gradient py-16 md:py-24 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full mb-6">
            <Landmark className="w-4 h-4 text-[#F7A81B]" />
            <span className="text-white/80 text-sm font-heading uppercase tracking-wider">
              El Monumento
            </span>
          </div>
          <h1 className="font-bold-sans text-4xl sm:text-5xl md:text-6xl text-white mb-4 tracking-tight">
            El Reloj Solar
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
            Un legado bajo el sol - Arte, ciencia y sostenibilidad
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 md:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          
          {/* Introduction with 3D Model Viewer */}
          <section className="mb-16 md:mb-24" data-testid="monumento-intro">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 md:order-1">
                <div className="divider-gold mb-6 mx-0" style={{ marginLeft: 0 }} />
                <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-6">
                  Un Icono de Acero Inoxidable
                </h2>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  El Reloj Solar del Rotary Club de Zaragoza es una obra monumental de 2.5 metros 
                  de altura, construida en acero inoxidable, que une arte, ciencia y sostenibilidad 
                  en un solo diseño.
                </p>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Inspirado en el logotipo de Rotary International, este reloj no solo mide el tiempo, 
                  sino que representa los valores que cambian el mundo: compañerismo, integridad, 
                  diversidad, liderazgo y servicio.
                </p>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Inaugurado en 2026 para conmemorar el centenario del club, el reloj marca las horas 
                  solares, los solsticios y equinoccios, conectando a Zaragoza con la Red de Relojes 
                  del Eclipse.
                </p>

                {/* Locate Button - Force window.open */}
                <Button 
                  className="btn-primary rounded-xl"
                  data-testid="monumento-locate-btn"
                  onClick={() => {
                    window.open(GOOGLE_MAPS_URL, '_blank');
                  }}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Ver Ubicación
                  <ExternalLink className="w-4 h-4 ml-2 opacity-70" />
                </Button>
              </div>

              {/* 3D Model Viewer */}
              <div className="order-1 md:order-2" data-testid="3d-model-container">
                <div className="relative bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-4 shadow-xl">
                  <model-viewer
                    src={ASSETS.clock3DModel}
                    alt="Modelo 3D del Reloj Solar Rotary"
                    auto-rotate="true"
                    camera-controls="true"
                    shadow-intensity="0.5"
                    shadow-softness="1"
                    style={{
                      width: '100%',
                      height: '500px',
                      backgroundColor: 'transparent',
                      borderRadius: '1rem'
                    }}
                    data-testid="3d-model-viewer"
                  >
                    <div className="absolute bottom-4 left-4 right-4 text-center">
                      <p className="text-xs text-slate-500 font-heading">
                        Arrastra para rotar • Pellizca para zoom
                      </p>
                    </div>
                  </model-viewer>
                  
                  {/* Decorative corners */}
                  <div className="absolute -top-2 -left-2 w-8 h-8 border-t-4 border-l-4 border-[#F7A81B] rounded-tl-lg" />
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-4 border-r-4 border-[#F7A81B] rounded-br-lg" />
                </div>
              </div>
            </div>
          </section>

          {/* Specifications with Glassmorphism */}
          <section className="mb-16 md:mb-24" data-testid="monumento-specs">
            <div className="text-center mb-12">
              <div className="divider-gold mb-6" />
              <h2 className="font-heading text-2xl md:text-3xl text-slate-800">
                Especificaciones Técnicas
              </h2>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {specifications.map((spec, index) => {
                const Icon = spec.icon;
                return (
                  <div 
                    key={index}
                    className="card-glass p-6 rounded-2xl text-center"
                    data-testid={`spec-${index}`}
                  >
                    <div className="w-14 h-14 mx-auto mb-4 bg-[#013E7F]/10 rounded-2xl flex items-center justify-center">
                      <Icon className="w-7 h-7 text-[#013E7F]" />
                    </div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-heading mb-1">
                      {spec.label}
                    </p>
                    <p className="font-heading text-lg font-semibold text-slate-800 mb-2">
                      {spec.value}
                    </p>
                    <p className="text-slate-600 text-sm">
                      {spec.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Values Section */}
          <section className="mb-16 md:mb-24 bg-gradient-to-br from-amber-50/50 to-orange-50/30 -mx-4 px-4 py-12 md:py-16 rounded-3xl" data-testid="monumento-values">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <div className="divider-gold mb-6" />
                <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-4">
                  Valores que Representa
                </h2>
                <p className="text-slate-600">
                  Un reloj que no solo mide el tiempo, sino los valores que cambian el mundo
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {["Compañerismo", "Integridad", "Diversidad", "Liderazgo", "Servicio"].map((value, index) => (
                  <div 
                    key={index}
                    className="glass-panel p-4 rounded-xl text-center hover:border-[#F7A81B] transition-all duration-300"
                  >
                    <Award className="w-6 h-6 text-[#F7A81B] mx-auto mb-2" />
                    <p className="font-heading text-sm font-medium text-slate-700">
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Gallery */}
          <section data-testid="monumento-gallery">
            <div className="text-center mb-10">
              <div className="divider-gold mb-6" />
              <h2 className="font-heading text-2xl md:text-3xl text-slate-800 mb-4">
                Galería de Imágenes
              </h2>
              <p className="text-slate-600">
                Explora el diseño y la ubicación del reloj solar
              </p>
            </div>
            
            <Gallery images={galleryImages} />
          </section>
        </div>
      </main>
    </div>
  );
};

export default Monumento;
